import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment/moment";
import momentTimezone from "moment-timezone";
import { uniqBy } from "lodash";
import { Row, Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import { CustomRepentanceModal } from "../../CommonContacts/Accessibility/CustomRepentanceModal";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import DatePicker from "../../../ui-lib/components/DateAndTime/DatePicker";
import TimePicker from "../../../ui-lib/components/DateAndTime/TimePicker";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import useTranslations from "../../../core/i18n/useTranslations";
import { CustomSelectedDays } from "../../../ui-lib/components/Scheduler/CustomSelectedDays";
import { IDropdownItem } from "../../../ui-lib/components/Dropdown/DropdownItem";
import {
  IActivityCreate,
  IActivityData,
  IActivityForm,
  IStatusEvent,
} from "../../../core/api/objects/types";
import { getPreselectedRepeatSchedule } from "../../../ui-lib/utils/schedulerHelper";
import { useRepentanceDropdown } from "../../../core/hooks/useRepentanceDropdown";
import {
  IExpectedUserSettings,
  IOrgIntegration,
} from "../../../core/api/organizations/types";
import NumberInput from "../../../ui-lib/components/Inputs/NumberInput";
import useSharedObject from "../SharedObjectContext/useSharedObject";
import useUser from "../../../core/user/useUser";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import { getErrorKey } from "../../../components/Errors/ErrorAlert";
import { toCronString } from "../../../ui-lib/utils/cronParser";
import {
  createActivity,
  getActivity,
  updateActivity,
} from "../../../core/api/objects/objects";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import LoadingSpinner from "../../../ui-lib/components/Loading/LoadingSpinner";
import { CurrentActivityHistory } from "./CurrentActivityHistory";
import Alert from "../../../ui-lib/components/Alerts/Alert";
import { userDateFormat } from "../../../ui-lib/utils/userDateFormat";

export const NewActivityModal = ({
  isOpen,
  integrations = [],
  userId,
  activityId,
  canEdit,
  onClose,
}: {
  userId: number;
  activityId?: string;
  isOpen: boolean;
  canEdit: boolean;
  integrations?: IOrgIntegration[];
  onClose: (refetch?: boolean) => void;
}) => {
  const t = useTranslations();
  const { data: objectData } = useSharedObject();
  const { authenticatedRequest, config } = useUser();
  const timezone = config?.timezone.timezoneName ?? "Europe/Stockholm";

  const {
    repentanceDropdownItems,
    setRepentanceDropdownItems,
    setSelectedDropdownItems,
  } = useRepentanceDropdown(0, true);

  const [isCustomModalOpened, setIsCustomModalOpened] = useState(false);
  const [showCustomSelectedDays, setShowCustomSelectedDays] = useState(false);
  const [integrationSectionItems, setIntegrationSectionItems] = useState<
    IDropdownItem[]
  >([]);
  const [integrationItems, setIntegrationItems] = useState<IDropdownItem[]>([]);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IOrgIntegration>();
  const [timingItems, setTimingItems] = useState<IDropdownItem[]>([]);
  const [customSettings, setCustomSettings] = useState<IExpectedUserSettings[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentActivityHistory, setCurrentActivityHistory] =
    useState<IStatusEvent[]>();
  const [defaultSelectedDays, setDefaultSelectedDays] = useState<number[]>([]);
  const [defaultEndTime, setDefaultEndTime] = useState<Date>();
  const [isDSTChangeDay, setIsDSTChangeDay] = useState(false);

  const createActivityPermission = config?.show.includes(
    "PlanningEngineActivityWrite"
  );

  const {
    formState: { errors, isDirty },
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    clearErrors,
    reset,
    watch,
  } = useForm<IActivityForm>();

  const selectedTimingType = watch("timingType");
  const repeatDays = watch("repeatDays");
  const selectedStartDate = watch("startDate")
    ? moment(watch("startDate"))
    : null;
  const selectedIntegrationType = watch("integrationType");

  const getLastSundayOfMarch = () => {
    if (!selectedStartDate) return;
    const year = selectedStartDate.year();
    const lastDayOfMarch = moment({ year, month: 2, day: 31 });

    return lastDayOfMarch.endOf("month").day(0).startOf("day").valueOf();
  };

  const fillFormData = (data: IActivityData) => {
    const resetObject = {
      integrationType: data.integrationType,
      integration: data.integration,
      description: data.description,
      timingType: data.timingType,
    } as any;
    if (data.timingType === "Recurring") {
      const timingData = data.timingInformation?.split(" ");
      const repeatDaysData = timingData[4].split(",").map((i) => +i);
      resetObject.repeatDays = repeatDaysData;
      resetObject.startTime = `${timingData[1]}:${timingData[0]}`;
      resetObject.startDate = moment.tz(data.startDate, timezone).toDate();
      if (data.endDate) {
        resetObject.endDate = moment.tz(data.endDate, timezone).toDate();
      }
      const selectedRepeatSchedule = getPreselectedRepeatSchedule(
        repeatDaysData,
        data.endDate,
        true
      );
      setDefaultEndTime(resetObject.endDate);
      setSelectedDropdownItems(selectedRepeatSchedule);
      setShowCustomSelectedDays(selectedRepeatSchedule === 5);
      setDefaultSelectedDays(repeatDaysData);
    }
    if (data.timingType === "Date") {
      resetObject.startTime = momentTimezone
        .tz(data.timingInformation, timezone)
        .format("HH:mm");
      resetObject.startDate = momentTimezone
        .tz(data.timingInformation, timezone)
        .toDate(); // PrimeReact DatePicker expects a Date object
    }
    setIntegrationSectionItems(
      integrations.map((i) => ({
        id: i.integrationId,
        name: i.integrationType,
        isSelected: i.integrationType === data.integrationType,
      }))
    );
    setIntegrationItems(
      integrations
        .filter((i) => i.integrationType === data.integrationType)
        .map((i: IOrgIntegration) => ({
          id: i.integrationId,
          name: i.name,
          isSelected: i.integrationId === data.integration,
        }))
    );
    setTimingItems(
      integrations
        .find((i) => i.integrationId === data.integration)
        ?.supportedTimingTypes.map((i: string) => ({
          id: i,
          name: t(`Objects:${i}`),
          isSelected: i === data.timingType,
        })) ?? []
    );
    const activityUserSettings =
      integrations.find((i) => i.integrationId === data.integration)
        ?.expectedUserSettings ?? [];
    activityUserSettings.forEach((setting) => {
      resetObject.userSettings = {
        ...resetObject.userSettings,
        [setting.parameterName]: data.userSettings[setting.parameterName],
      };
    });
    setCustomSettings(activityUserSettings);
    setCurrentActivityHistory(data.statusEvents);
    reset(resetObject);
  };

  const fetchActivityData = async () => {
    if (activityId) {
      setIsLoading(true);
      try {
        const { data } = await getActivity(
          userId,
          activityId,
          timezone,
          authenticatedRequest
        );
        fillFormData(data);
      } catch (error: any) {
        notifyApiErrors(error.response?.data?.errors);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) fetchActivityData();
  }, [isOpen]);

  useEffect(() => {
    if (selectedStartDate) {
      setIsDSTChangeDay(selectedStartDate.valueOf() === getLastSundayOfMarch());
    }
  }, [selectedStartDate]);

  const onCloseWrapper = (refetch = false) => {
    setRepentanceDropdownItems(
      repentanceDropdownItems.map((item) => ({
        ...item,
        isSelected: item.id === 0,
      }))
    );
    setShowCustomSelectedDays(false);
    setIntegrationSectionItems(
      integrationSectionItems.map((i) => ({ ...i, isSelected: false }))
    );
    customSettings.forEach((setting) => {
      setValue(`userSettings.${setting.parameterName}`, "");
    });
    setIntegrationItems([]);
    setTimingItems([]);
    setDefaultSelectedDays([]);
    setCurrentActivityHistory(undefined);
    reset({ description: "" });
    onClose(refetch);
  };

  const onSubmit = async (data: IActivityForm) => {
    setIsLoading(true);
    let cronString;
    let startDate;
    let endDate;
    const [hours, minutes] = (data.startTime ?? "").split(":");

    if (selectedTimingType !== "Now") {
      startDate = momentTimezone(data.startDate)
        .tz(timezone)
        .set("hours", +hours)
        .set("minutes", +minutes)
        .set("seconds", 0)
        .format();
      if (data.endDate) {
        endDate = momentTimezone(data.endDate)
          .tz(timezone)
          .set("hours", 23)
          .set("minutes", 59)
          .set("seconds", 59)
          .format();
      }
    }
    if (selectedTimingType === "Recurring") {
      const defaultDaysToUse = defaultSelectedDays.length
        ? defaultSelectedDays
        : [1, 2, 3, 4, 5, 6, 7];
      cronString = toCronString(
        data.startTime,
        data.repeatDays?.length ? data.repeatDays : defaultDaysToUse
      );
    }
    const dataToSubmit: IActivityCreate = {
      startDate,
      endDate,
      integration: data.integration,
      description: data.description,
      timezoneId: timezone,
      timingType: data.timingType,
      organizationId: objectData!.organizationId,
      timingInformation: cronString ?? startDate,
      userSettings: data.userSettings,
    };
    try {
      if (objectData?.id) {
        if (activityId) {
          await updateActivity(
            objectData.id,
            activityId,
            dataToSubmit,
            authenticatedRequest
          );
        } else {
          await createActivity(
            objectData.id,
            dataToSubmit,
            authenticatedRequest
          );
        }
      }
      onCloseWrapper(true);
    } catch (error: any) {
      notify({
        message: t(`Errors:${getErrorKey(error)}`),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseCustomRecurrence = () => {
    reset((values) => ({
      ...values,
      endDate: defaultEndTime,
      repeatDays: defaultSelectedDays,
    }));
    setIsCustomModalOpened(false);
  };

  useEffect(() => {
    if (integrations.length && !integrationSectionItems.length) {
      const uniqueIntegrationTypes = uniqBy(
        integrations,
        "integrationType"
      ).map((integration: IOrgIntegration) => ({
        id: integration.integrationId,
        name: integration.integrationType,
        isSelected: false,
      }));
      if (uniqueIntegrationTypes.length === 1) {
        setValue("integrationType", integrations[0].integrationType);
        uniqueIntegrationTypes[0].isSelected = true;
      }
      setIntegrationSectionItems(uniqueIntegrationTypes);
    }
  }, [integrations]);

  useEffect(() => {
    if (selectedIntegrationType) {
      const filteredIntegrations = integrations.filter(
        (i) => i.integrationType === selectedIntegrationType
      );
      setIntegrationItems(
        filteredIntegrations.map((i: IOrgIntegration) => ({
          id: i.integrationId,
          name: i.name,
          isSelected: filteredIntegrations.length === 1,
        }))
      );
      if (filteredIntegrations.length === 1) {
        setValue("integration", filteredIntegrations[0].integrationId);
        setSelectedIntegration(filteredIntegrations[0]);
      } else {
        setTimingItems([]);
        setValue("integration", "");
        setValue("timingType", "");
        setSelectedIntegration(undefined);
        setCustomSettings([]);
      }
    }
  }, [selectedIntegrationType]);

  useEffect(() => {
    setIntegrationItems(
      integrationItems.map((i) => ({
        ...i,
        isSelected: i.id === selectedIntegration?.integrationId,
      }))
    );
    setTimingItems(
      selectedIntegration?.supportedTimingTypes.map((i: string) => ({
        id: i,
        name: t(`Objects:${i}`),
        isSelected: false,
      })) ?? []
    );
    setCustomSettings(selectedIntegration?.expectedUserSettings ?? []);
  }, [selectedIntegration]);

  const validateCustomSettingField = (
    setting: IExpectedUserSettings,
    type?: string
  ): string => {
    if (type === "required") {
      return t("Errors:input_field_required");
    }
    if (type === "pattern") {
      return t("Errors:input_field_invalid").replace(
        "{0}",
        setting.regex ?? ""
      );
    }
    if (type === "max") {
      return t("Errors:input_field_max_length").replace(
        "{0}",
        setting.maximum?.toString() ?? ""
      );
    }
    if (type === "min") {
      return t("Errors:input_field_min_length").replace(
        "{0}",
        setting.minimum?.toString() ?? ""
      );
    }
    return t("Errors:wrong_format");
  };

  const getModalHeader = () => {
    if (!activityId) {
      return t("Objects:plan_new_activity");
    }

    return canEdit ? t("Objects:edit_activity") : t("Objects:view_activity");
  };

  const getModalDescription = () => {
    if (!activityId) {
      return t("Objects:new_activity_description");
    }

    return canEdit
      ? t("Objects:edit_activity_description")
      : t("Objects:view_activity_description");
  };

  return (
    <>
      <PrimeModal
        withHeader
        withFooter
        fixedWidth="848px"
        header={getModalHeader()}
        isOpen={isOpen}
        onClose={() => onCloseWrapper()}
        submitBtn={{
          text: activityId
            ? t("Common:save_changes")
            : t("Objects:plan_new_activity"),
          variant: "primary",
          onClick: handleSubmit(onSubmit),
          disabled: isLoading || !isDirty,
          hidden: !canEdit || !createActivityPermission,
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: () => {
            reset();
            onCloseWrapper();
          },
        }}
      >
        {isLoading ? (
          <LoadingSpinner theme="primary" />
        ) : (
          <>
            <form>
              <p>{getModalDescription()}</p>
              <Spacer size={16} />
              <Row>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="integrationType"
                  render={({ field }) => (
                    <Dropdown
                      required={integrationSectionItems.length > 1}
                      title={t("Common:integration")}
                      selectedItem={integrationSectionItems.find(
                        (i) => i.isSelected
                      )}
                      items={integrationSectionItems}
                      onSelectItem={(e) => {
                        field.onChange(e.name);
                        setIntegrationSectionItems(
                          integrationSectionItems.map((i) => ({
                            ...i,
                            isSelected: i.name === e.name,
                          }))
                        );
                      }}
                      disabled={
                        integrationSectionItems.length < 2 ||
                        !canEdit ||
                        !!activityId
                      }
                      validationError={
                        errors.integrationType &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
                <Spacer size={32} />
                <Controller
                  name="integration"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      required={integrationItems.length > 1}
                      disabled={
                        integrationItems.length < 2 || !canEdit || !!activityId
                      }
                      items={integrationItems}
                      onSelectItem={(e) => {
                        setSelectedIntegration(
                          integrations.find((i) => i.integrationId === e.id)
                        );
                        field.onChange(e.id as string);
                      }}
                      selectedItem={integrationItems.find((i) => i.isSelected)}
                      title={t("Objects:activity_type")}
                      validationError={
                        errors.integration && t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </Row>
              <Spacer size={16} />
              <TwoColGrid>
                <Controller
                  name="timingType"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      required
                      disabled={!timingItems.length || !canEdit || !!activityId}
                      items={timingItems}
                      onSelectItem={(e) => {
                        field.onChange(e.id as string, {
                          shouldDirty: true,
                        });
                        setTimingItems(
                          timingItems.map((i) => ({
                            ...i,
                            isSelected: i.id === e.id,
                          }))
                        );
                        if (e.id === "Recurring") {
                          setValue("repeatDays", [1, 2, 3, 4, 5, 6, 7]);
                        } else {
                          setValue("repeatDays", []);
                        }
                      }}
                      selectedItem={timingItems.find((i) => i.isSelected)}
                      title={t("Objects:timing_type")}
                      validationError={
                        errors.timingType && t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </TwoColGrid>
              <Spacer size={16} />
              {selectedTimingType === "Recurring" && (
                <>
                  <TwoColGrid style={{ alignItems: "center" }}>
                    <Controller
                      name="repeatDays"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          required
                          disabled={!canEdit}
                          onSelectItem={(item) => {
                            if (item.id !== 5) {
                              field.onChange(undefined);
                              setShowCustomSelectedDays(false);
                            }
                            switch (item.id) {
                              case 0:
                                field.onChange([1, 2, 3, 4, 5, 6, 7]);
                                break;
                              case 1:
                                field.onChange([]);
                                break;
                              case 2:
                                field.onChange([new Date().getDay() + 1]);
                                break;
                              case 3:
                                field.onChange([2, 3, 4, 5, 6]);
                                break;
                              case 4:
                                field.onChange([1, 7]);
                                break;
                              case 5:
                                setIsCustomModalOpened(true);
                                if (
                                  repentanceDropdownItems.find(
                                    (i) => i.isSelected
                                  )!.id !== 5
                                ) {
                                  field.onChange([]);
                                }
                                setShowCustomSelectedDays(true);
                                break;
                              default:
                                setShowCustomSelectedDays(false);
                                break;
                            }
                            setSelectedDropdownItems(+item.id);
                          }}
                          selectedItem={repentanceDropdownItems.find(
                            (item) => item.isSelected
                          )}
                          items={repentanceDropdownItems}
                          title={t("Contacts:repeat")}
                          maxVisible={6}
                        />
                      )}
                    />
                    {showCustomSelectedDays && (
                      <CustomSelectedDays
                        repeatDays={repeatDays ?? defaultSelectedDays}
                        isFormEditable={true}
                        endDateValue={getValues("endDate") as any}
                        startFromSunday
                      />
                    )}
                  </TwoColGrid>
                  <Spacer size={16} />
                </>
              )}
              {(selectedTimingType === "Date" ||
                selectedTimingType === "Recurring") && (
                <>
                  <Row align="start">
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) =>
                        canEdit ? (
                          <DatePicker
                            required
                            minDate={moment().toDate()}
                            maxDate={
                              getValues("endDate")
                                ? moment(getValues("endDate"))?.toDate()
                                : undefined
                            }
                            value={field.value}
                            onChange={field.onChange}
                            label={t("Common:select_start_date")}
                            dateFormat={userDateFormat(
                              config?.dateFormat ?? "YYYY-MM-DD"
                            )}
                            validationError={
                              errors.startDate &&
                              t("Errors:input_field_required")
                            }
                          />
                        ) : (
                          <TextInput
                            disabled
                            inputClassName="fw-600"
                            label={t("Common:select_start_date")}
                            value={momentTimezone
                              .tz(field.value, timezone)
                              .format(
                                userDateFormat(
                                  config?.dateFormat ?? "YYYY-MM-DD"
                                )
                              )}
                          />
                        )
                      }
                    />
                    <Spacer size={32} />
                    <Controller
                      name="startTime"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TimePicker
                          required
                          description={t(
                            "Objects:start_activity_time_description"
                          )}
                          disabled={!canEdit}
                          value={field.value}
                          onChange={field.onChange}
                          label={t("Contacts:input_start_time")}
                          validationError={
                            errors.startTime && t("Errors:input_field_required")
                          }
                        />
                      )}
                    />
                  </Row>
                  {isDSTChangeDay && repeatDays?.includes(1) && (
                    <>
                      <Spacer size={16} />
                      <Alert
                        withDefaultIcon
                        title={t("Objects:dst_change_day_alert")}
                        variant="warning"
                      />
                    </>
                  )}
                  <Spacer size={16} />
                </>
              )}

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextInput
                    disabled={!canEdit}
                    label={t("Objects:planned_additional_info")}
                    placeholder={
                      canEdit ? t("Objects:activity_details_placeholder") : ""
                    }
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {customSettings.map((setting) => {
                switch (setting.settingType) {
                  case "String":
                    return (
                      <Fragment key={`${setting.parameterName}_string`}>
                        <Spacer size={16} />
                        <Controller
                          rules={{
                            required: setting.required,
                            pattern: new RegExp(setting.regex ?? ""),
                          }}
                          name={`userSettings.${setting.parameterName}`}
                          control={control}
                          defaultValue=""
                          render={({ field }) => {
                            console.log(setting);
                            console.log(new RegExp(setting.regex ?? ""));
                            return (
                              <TextInput
                                required={setting.required}
                                inputRef={field.ref}
                                disabled={!canEdit}
                                label={setting.displayName}
                                value={field.value}
                                onChange={field.onChange}
                                validationError={
                                  errors.userSettings?.[
                                    setting.parameterName
                                  ] &&
                                  validateCustomSettingField(
                                    setting,
                                    errors.userSettings?.[setting.parameterName]
                                      ?.type
                                  )
                                }
                              />
                            );
                          }}
                        />
                      </Fragment>
                    );
                  case "Int":
                    return (
                      <Fragment key={`${setting.parameterName}_int`}>
                        <Spacer size={16} />
                        <Controller
                          control={control}
                          rules={{
                            required: setting.required,
                            min: setting.minimum,
                            max: setting.maximum,
                          }}
                          defaultValue={""}
                          name={`userSettings.${setting.parameterName}`}
                          render={({ field }) => (
                            <NumberInput
                              required={setting.required}
                              inputRef={field.ref}
                              disabled={!canEdit}
                              label={setting.displayName}
                              value={field.value?.toString()}
                              onChange={(val) => {
                                field.onChange(val);
                                trigger(field.name);
                              }}
                              validationError={
                                errors.userSettings?.[setting.parameterName] &&
                                validateCustomSettingField(
                                  setting,
                                  errors.userSettings?.[setting.parameterName]
                                    ?.type
                                )
                              }
                            />
                          )}
                        />
                      </Fragment>
                    );
                  case "Enum":
                    return (
                      <Fragment key={`${setting.parameterName}_enum`}>
                        <Spacer size={16} />
                        <Controller
                          control={control}
                          rules={{ required: setting.required }}
                          name={`userSettings.${setting.parameterName}`}
                          defaultValue={""}
                          render={({ field }) => {
                            const items = setting.selectableEnums!.map((i) => ({
                              id: i,
                              name: i,
                              isSelected: i === field.value,
                            }));
                            return (
                              <Dropdown
                                required={setting.required}
                                disabled={!canEdit}
                                title={setting.displayName}
                                selectedItem={items.find((i) => i.isSelected)}
                                items={items}
                                onSelectItem={(item) => {
                                  field.onChange(item.id as string);
                                }}
                                validationError={
                                  errors.userSettings?.[
                                    setting.parameterName
                                  ] &&
                                  validateCustomSettingField(
                                    setting,
                                    errors.userSettings?.[setting.parameterName]
                                      ?.type
                                  )
                                }
                              />
                            );
                          }}
                        />
                      </Fragment>
                    );
                  case "Dynamic":
                    return (
                      <Fragment key={`${setting.parameterName}_dynamic`}>
                        <Spacer size={16} />
                        <Controller
                          rules={{ required: setting.required }}
                          name={`userSettings.${setting.parameterName}`}
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <TextInput
                              required={setting.required}
                              label={setting.displayName}
                              value={field.value}
                              onChange={field.onChange}
                              disabled={!canEdit}
                              validationError={
                                errors.userSettings?.[setting.parameterName] &&
                                validateCustomSettingField(
                                  setting,
                                  errors.userSettings?.[setting.parameterName]
                                    ?.type
                                )
                              }
                            />
                          )}
                        />
                      </Fragment>
                    );
                }
              })}
              <Spacer size={2} />
            </form>
            {activityId && (
              <CurrentActivityHistory statuses={currentActivityHistory ?? []} />
            )}
          </>
        )}
      </PrimeModal>
      <PrimeModal
        withHeader
        withFooter
        fixedWidth="848px"
        header={t("Contacts:custom_recurrence")}
        isOpen={isCustomModalOpened}
        onClose={onCloseCustomRecurrence}
        submitBtn={{
          disabled: isLoading,
          text: t("Common:save"),
          variant: "primary",
          onClick: () => {
            trigger("endDate").then(() => {
              if (!errors.endDate) {
                setIsCustomModalOpened(false);
              }
            });
          },
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: onCloseCustomRecurrence,
        }}
      >
        <CustomRepentanceModal
          control={control}
          errors={errors}
          namePrefix=""
          setValue={setValue}
          getValues={getValues}
          trigger={trigger}
          clearErrors={clearErrors}
          startFromSunday
          alignWithCron
        />
      </PrimeModal>
    </>
  );
};
